import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import { EyeBlueIcon } from "../../../../assets";
import { OrganisationTypeConstants, UserType } from "../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { downloadProjectDocument } from "../../../../service/project";
import { getProjectDocuments, GetProjectDocumentsResponse } from "../../../../service/publicquery";
import { searchProjectDocuments, SearchProjectDocumentsResponse } from "../../../../service/query";
import { ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject, initiateDocumentDownloadViaBrowser, useDocumentUploadPermission } from "../../../../utils";
import { getDocumentHistoryRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../../../widget";
import { IconCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { downloadDocumentCellFormatter } from "../../../developer/activities";
import { uploadDocumentCellFormatter } from "../../projects/project/documents/components";
import { LandholdingContext } from "../LandholdingContext";

interface UseGroupDocumentsReturnData {
  downloadDocument: (projectDocumentHistoryUuid: string) => Promise<void>;
  isLoading: boolean;
  documents: GetProjectDocumentsResponse[] | undefined;
  columns: DataGridColumnDefinition[];
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  uploadNewDocument: () => void;
  uploadDocumentUuid?: string;
  latestDocumentVersionUuid?: string;
  refreshDocuments: boolean;
  setRefreshDocuments: Dispatch<SetStateAction<boolean>>;
  showUploadModal: boolean;
  setShowUploadModal: Dispatch<SetStateAction<boolean>>;
  landholdingUuid: string | undefined;
  hasManageProjectDocumentPermissionForGroup: boolean;
  projectUuid: string | null;
  currentUserType: UserType;
  hasFullAccess: boolean;
}

export const useGroupDocuments = (): UseGroupDocumentsReturnData => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserType, user } = useAuth();
  const { landholdingUuid, landholdingDetails, publicLandholdingDetails } = useContext(LandholdingContext);
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocumentUuid, setUploadDocumentUuid] = useState<string>();
  const [latestDocumentVersionUuid, setLatestDocumentVersionUuid] = useState<string>();
  const [refreshDocuments, setRefreshDocuments] = useState(false);
  const [projectUuid, setProjectUuid] = useState<string | null>(null);
  const [documents, setDocuments] = useState<GetProjectDocumentsResponse[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /*
    A landholding's documents are project documents held against the group, so the upload grant is read the
    same way every other document surface reads it (KAN-9638). The landholding uuid IS the group uuid.
  */
  const { canUploadDocument: hasManageProjectDocumentPermissionForGroup } = useDocumentUploadPermission(
    landholdingUuid ? { scope: "group", uuid: landholdingUuid } : undefined,
    user?.userUuid
  );

  const uploadNewDocument = (): void => {
    setUploadDocumentUuid(undefined);
    setLatestDocumentVersionUuid(undefined);
    setShowUploadModal(true);
  };

  const downloadDocument = async (projectDocumentHistoryUuid: string): Promise<void> => {
    const res = await downloadProjectDocument({ projectDocumentHistoryUuid });

    initiateDocumentDownloadViaBrowser(res);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    { key: "type", name: "Type", dataType: "string", formatter: "stringWithEllipsis" },
    {
      key: "variant",
      name: "Variant",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: false,
    },
    {
      key: "projectOrGroup",
      name: "Project / Landholding",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: false,
    },
    {
      key: "version",
      name: "Version",
      dataType: "number",
      formatter: "align",
      filterable: true,
    },
    {
      key: "updatedAt",
      name: "Last updated",
      dataType: "Date",
      formatter: "dateOnly",
      filterable: false,
    },
    {
      key: "updatedByUser.fullName",
      name: "Last updated by",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
    },
    {
      key: "detailsLink",
      name: "Details",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
    },
    {
      key: "downloadIcon",
      name: "Download latest",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      filterable: false,
      sortable: false,
      customCellFormatter: downloadDocumentCellFormatter,
    },
  ];

  if ([OrganisationTypeConstants.DEVELOPER, OrganisationTypeConstants.DEVELOPMENT_MANAGER].includes(currentUserType))
    columns.push({
      key: "uploadIcon",
      name: "Upload new version",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 140,
      customCellFormatter: uploadDocumentCellFormatter,
    });

  const formatData = useCallback((responseData: SearchProjectDocumentsResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d);

        result.variant = d.customVariant || d.variant;

        result.projectOrGroup = d.groupName ?? d.projectName;

        result.downloadIcon = {
          action: async () => {
            const res = await downloadProjectDocument({ projectDocumentHistoryUuid: d.versionUuid });
            initiateDocumentDownloadViaBrowser(res);
          },
          text: "Download latest",
        } as DataGridButtonLinkCellFormatterData;

        result.uploadIcon = {
          action: () => {
            setProjectUuid(d.projectUuid);
            setUploadDocumentUuid(d.uuid);
            setLatestDocumentVersionUuid(d.versionUuid);
            setShowUploadModal(true);
          },
          text: "Upload new version",
        } as DataGridButtonLinkCellFormatterData;

        result.detailsLink = {
          action: () => {
            const extra = d.groupId
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                { groupUuid: landholdingUuid!, groupName: landholdingDetails!.groupName, isNonStandard: true }
              : null;

            navigate(
              getDocumentHistoryRoute(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                d.groupId ? landholdingDetails!.projects[0].uuid : d.projectUuid!,
                d.uuid,
                currentUserType,
                undefined,
                d.groupId ? landholdingUuid : undefined
              ),
              {
                state: {
                  origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin,
                  extra,
                },
              }
            );
          },
          icon: <EyeBlueIcon width={20} height={20} strokeWidth={1.5} />,
        } as IconCellFormatterData;
        return result;
      }) || []
    );
  }, []);

  const defaultSortingCriteria: SortColumn[] = [
    { columnKey: "groupName", direction: "ASC" },
    { columnKey: "updatedAt", direction: "DESC" },
  ];

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (landholdingDetails) {
      await searchProjectDocuments({
        groupUuid: landholdingUuid,
        paging: {
          limit: paging.pageSize,
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: s.key as any,
          direction: s.direction,
        })),
        filter: { results: dataGridMapFilterCriteria(filtering) },
        projectUuid: null,
      })
        .then((response) => {
          data = {
            resultData: formatData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize || 10,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            },
          };
        })
        .finally(() => {
          setDataIsLoading(false);
        });
    }
    return data;
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (publicLandholdingDetails) {
        await getProjectDocuments({
          projectUuid: null,
          groupUuid: landholdingUuid ?? null,
        })
          .then((response) => {
            if (response.status === Status.Success) {
              setDocuments(response.data);
            }
          })
          .finally(() => setIsLoading(false));
      }
    };

    fetchData();
  }, [publicLandholdingDetails?.groupGuid]);

  return {
    downloadDocument,
    isLoading,
    documents,
    columns,
    onChange,
    defaultSortingCriteria,
    dataIsLoading,
    uploadNewDocument,
    uploadDocumentUuid,
    latestDocumentVersionUuid,
    refreshDocuments,
    setRefreshDocuments,
    showUploadModal,
    setShowUploadModal,
    landholdingUuid,
    hasManageProjectDocumentPermissionForGroup,
    projectUuid,
    currentUserType,
    hasFullAccess: !!landholdingDetails,
  };
};
